import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, KeyRound, ShieldAlert } from "lucide-react";
import { api, errMsg, fmtDate } from "../lib/api";
import { PageHeader, Panel, Empty, inputCls } from "../components/ui-kit";
import { StatusBadge, RoleBadge } from "../components/Badges";

const RequestRow = ({ r, onDone }) => {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (action) => {
    if (action === "approve" && !window.confirm(`Approve the new password for ${r.name}? Their old password will stop working and they'll be signed out everywhere.`)) return;
    setBusy(true);
    try {
      await api.post(`/password-requests/${r.id}/${action}`, action === "reject" ? { reason } : undefined);
      toast.success(action === "approve" ? `Password change approved for ${r.name}` : "Request rejected");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
      onDone();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel data-testid={`password-request-row-${r.id}`} className="p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{r.name}</span>
            {r.user_is_admin ? <span className="font-mono text-[10px] uppercase text-orange-300">Super Admin</span> : r.user_roles?.map((k) => <RoleBadge key={k} role={k} />)}
          </div>
          <div className="truncate text-xs text-slate-500">{r.email}</div>
          <div className="mt-1 font-mono text-[10px] text-slate-600">Requested {fmtDate(r.created_at)} · expires {fmtDate(r.expires_at)}</div>
        </div>
        {r.can_decide ? (
          <div className="flex flex-wrap gap-2">
            <button data-testid={`password-request-approve-${r.id}`} disabled={busy} onClick={() => decide("approve")} className="inline-flex items-center gap-1 rounded-lg bg-emerald-500/15 px-3 py-2 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-50"><Check className="h-3.5 w-3.5" />Approve</button>
            <button data-testid={`password-request-reject-${r.id}`} disabled={busy} onClick={() => setRejecting(!rejecting)} className="inline-flex items-center gap-1 rounded-lg bg-rose-500/10 px-3 py-2 text-xs font-semibold text-rose-300 hover:bg-rose-500/20 disabled:opacity-50"><X className="h-3.5 w-3.5" />Reject</button>
          </div>
        ) : <span className="text-xs text-slate-500" data-testid={`password-request-locked-${r.id}`}>Needs the super admin</span>}
      </div>
      {rejecting && (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <input data-testid={`password-request-reason-${r.id}`} placeholder="Reason (optional)" className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} />
          <button data-testid={`password-request-confirm-reject-${r.id}`} disabled={busy} onClick={() => decide("reject")} className="shrink-0 rounded-lg bg-rose-500/20 px-4 py-2 text-sm font-semibold text-rose-200 hover:bg-rose-500/30">Confirm reject</button>
        </div>
      )}
    </Panel>
  );
};

const HistoryRow = ({ r }) => (
  <div data-testid={`password-request-history-${r.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-[#282E3D] px-2 py-3 text-sm last:border-0">
    <span className="min-w-0 flex-1 truncate"><b>{r.name}</b> <span className="text-xs text-slate-500">{r.email}</span></span>
    <StatusBadge status={r.status} />
    <span className="text-xs text-slate-500">{r.decided_by_name ? `by ${r.decided_by_name} · ` : ""}{fmtDate(r.decided_at || r.created_at)}</span>
    {r.reason && <span className="w-full text-xs text-slate-400">Reason: {r.reason}</span>}
  </div>
);

export default function PasswordRequests() {
  const [pending, setPending] = useState(null);
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState("");
  const load = () => {
    Promise.all([api.get("/password-requests?status=pending"), api.get("/password-requests?status=decided")])
      .then(([p, h]) => { setPending(p.data); setHistory(h.data); setErr(""); })
      .catch((e) => { setErr(errMsg(e)); toast.error("Couldn't load password requests"); });
  };
  useEffect(() => { load(); }, []);
  if (err && !pending) return <div className="text-rose-300" data-testid="password-requests-error">Couldn't load password requests: {err}</div>;
  return (
    <div>
      <PageHeader eyebrow="Security" title="Password requests" desc="Employees who used 'Forgot password' and are waiting for approval. The new password only starts working once you approve it." />
      <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4 text-sm text-amber-200" data-testid="password-requests-warning">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <span>Anyone who knows an email address can send a request. Before approving, check with the person (in person, by phone or on chat) that they really asked for it.</span>
      </div>
      <section className="mb-10">
        <h2 className="mb-4 font-heading text-lg font-semibold">Pending <span className="ml-1 font-mono text-sm text-amber-300" data-testid="password-requests-pending-count">{pending?.length ?? 0}</span></h2>
        {!pending ? <div className="text-slate-500">Loading…</div> : pending.length ? (
          <div className="space-y-3">{pending.map((r) => <RequestRow key={r.id} r={r} onDone={load} />)}</div>
        ) : <Empty icon={KeyRound} title="No pending requests" desc="Requests show up here when someone uses 'Forgot password'." testId="password-requests-empty" />}
      </section>
      {history.length > 0 && (
        <section>
          <h2 className="mb-4 font-heading text-lg font-semibold">Recent decisions</h2>
          <Panel className="px-3">{history.slice(0, 30).map((r) => <HistoryRow key={r.id} r={r} />)}</Panel>
        </section>
      )}
    </div>
  );
}
